import { useEffect, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { submitMediaRequest } from "@/lib/media-leads.functions";
import { captureAttribution, getAttribution } from "@/lib/attribution";
import { trackEvent } from "@/lib/analytics";

import { createFileRoute } from "@tanstack/react-router";
import { Nav } from "@/components/site/Nav";
import { Footer } from "@/components/site/Closing";
import heroImg from "@/assets/cc_main_hero.png";
import img1 from "@/assets/cc_img1_soccer.png";
import img2 from "@/assets/cc_img2_nfl.png";
import img3 from "@/assets/cc_img3_basketball.png";
import img5 from "@/assets/cc_img5_hockey.png";
import img6 from "@/assets/cc_img6_college_football.png";
import img8 from "@/assets/cc_img8_tennis.png";

export const Route = createFileRoute("/media")({
  head: () => ({
    meta: [
      { title: "Media Center | ClutchCube" },
      {
        name: "description",
        content:
          "Official media resources for ClutchCube, the world's first immersive sports simulation chamber.",
      },
      { property: "og:title", content: "Media Center | ClutchCube" },
      {
        property: "og:description",
        content:
          "Official media resources for ClutchCube, the world's first immersive sports simulation chamber.",
      },
      { property: "og:type", content: "website" },
      { property: "og:url", content: "https://clutchcube.com/media" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [{ rel: "canonical", href: "https://clutchcube.com/media" }],
  }),
  component: MediaPage,
});

const facts: { label: string; value: string }[] = [
  { label: "Company", value: "ClutchCube" },
  { label: "Industry", value: "Experiential Entertainment" },
  { label: "Category", value: "Immersive Sports Entertainment" },
  { label: "Founded", value: "2026" },
  { label: "Headquarters", value: "To be announced" },
  { label: "Founder", value: "Herman DeBoard" },
  { label: "Current Raise", value: "$10M" },
  { label: "Mission", value: "Transform spectators into participants." },
  {
    label: "Vision",
    value: "Build the world's leading immersive sports entertainment franchise.",
  },
];

const resources: { title: string; type: string; available: boolean }[] = [
  { title: "Company Logo", type: "PNG", available: false },
  { title: "Company Logo", type: "SVG", available: false },
  { title: "Founder Headshot", type: "JPG", available: false },
  { title: "Brand Guidelines", type: "PDF", available: false },
  { title: "Investor One-Pager", type: "PDF", available: false },
  { title: "Company Fact Sheet", type: "PDF", available: false },
  { title: "High-Res Marketing Images", type: "ZIP", available: false },
  { title: "Product Renderings", type: "ZIP", available: false },
  { title: "Press Release Archive", type: "PDF", available: false },
];

const speakingTopics = [
  "Podcasts",
  "Conferences",
  "Sports Technology",
  "Experience Economy",
  "Entrepreneurship",
  "Franchise Development",
  "AI & Immersive Technology",
  "Veteran Entrepreneurship",
];

const interviewTopics = [
  "The Future of the Experience Economy",
  "Reinventing Sports Entertainment",
  "Building the World's First Immersive Sports Simulation Chamber",
  "Franchising Experiential Entertainment",
  "Sports Technology Innovation",
  "Veteran Entrepreneurship",
  "Startup Fundraising",
  "Consumer Behavior & Experiences",
];

const requestTypes = [
  "Interview",
  "Podcast",
  "Article",
  "Television",
  "Investor Story",
  "Conference Speaker",
  "Press Kit",
  "Other",
];

function MediaPage() {
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [started, setStarted] = useState(false);
  const submitMedia = useServerFn(submitMediaRequest);

  useEffect(() => {
    captureAttribution();
  }, []);

  const onFirstInteraction = () => {
    if (started) return;
    setStarted(true);
    trackEvent("media_form_start", { form: "media_center" });
  };

  const onSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError(null);
    const fd = new FormData(e.currentTarget);
    setSubmitting(true);
    trackEvent("media_form_submit", { form: "media_center" });
    try {
      const res = await submitMedia({
        data: {
          name: String(fd.get("name") || ""),
          publication: String(fd.get("publication") || ""),
          email: String(fd.get("email") || ""),
          phone: String(fd.get("phone") || "") || null,
          website_url: String(fd.get("website") || "") || null,
          request_type: String(fd.get("requestType") || ""),
          message: String(fd.get("message") || "") || null,
          website: String(fd.get("hp_website") || ""),
          source: "media_center",
          ...getAttribution(),
        },
      });
      if (!res.ok) setError(res.error || "Something went wrong. Please try again.");
      else {
        trackEvent("media_request_success", { form: "media_center" });
        setSubmitted(true);
      }
    } catch (err) {
      setError((err as Error).message || "Network error. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };


  return (
    <div className="min-h-screen bg-background text-foreground">
      <Nav />
      <main>
        {/* HERO */}
        <section className="relative pt-32 pb-24 overflow-hidden border-b border-border">
          <img
            src={heroImg}
            alt=""
            aria-hidden
            className="absolute inset-0 w-full h-full object-cover opacity-25"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-background/60 via-background/85 to-background" />
          <div className="absolute inset-0 led-grid opacity-25" />
          <div className="relative mx-auto max-w-5xl px-6">
            <p className="text-xs uppercase tracking-[0.3em] text-primary mb-6">
              Media Center
            </p>
            <h1 className="font-display text-5xl sm:text-7xl lg:text-8xl leading-[0.95]">
              Media &amp; <span className="text-gradient-ember">Press Center</span>
            </h1>
            <p className="mt-8 text-lg sm:text-xl text-muted-foreground max-w-3xl leading-relaxed">
              Everything journalists, media outlets, podcast hosts, conference organizers, and
              content creators need to learn about ClutchCube.
            </p>
            <div className="mt-8 max-w-3xl space-y-4 text-[15px] leading-[1.75] text-muted-foreground">
              <p>
                ClutchCube is building the world&rsquo;s first immersive sports simulation
                chamber&mdash;a new category of experiential sports entertainment designed for
                the Experience Economy.
              </p>
              <p>
                We&rsquo;re happy to provide interviews, media assets, investor background, and
                additional information upon request.
              </p>
            </div>
            <div className="mt-10">
              <a
                href="#press-form"
                onClick={() => trackEvent("media_cta_click", { placement: "media_page" })}
                className="inline-flex items-center justify-center px-10 py-5 bg-primary text-primary-foreground font-semibold uppercase tracking-widest text-sm rounded-sm shadow-glow hover:translate-y-[-2px] transition"
              >
                Request Press Kit
              </a>
            </div>
          </div>
        </section>

        {/* ABOUT */}
        <section className="relative py-24 border-b border-border overflow-hidden">
          <div className="absolute inset-0 led-grid opacity-10" />
          <div className="relative mx-auto max-w-6xl px-6 grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <p className="text-xs uppercase tracking-[0.3em] text-primary mb-4">
                About ClutchCube
              </p>
              <h2 className="font-display text-4xl sm:text-5xl leading-[1.05]">
                A new category of sports entertainment.
              </h2>
              <div className="mt-8 space-y-5 text-[15px] leading-[1.8] text-muted-foreground">
                <p>
                  <span className="text-foreground font-medium">Mission.</span> Transform
                  spectators into participants by giving every fan the ability to step inside the
                  moments they&rsquo;ve always imagined.
                </p>
                <p>
                  <span className="text-foreground font-medium">What makes it unique.</span>{" "}
                  ClutchCube combines volumetric video, spatial audio, motion tracking, and
                  purpose-built architecture into a single immersive chamber&mdash;an experience
                  that cannot be replicated at home, in a stadium, or through traditional VR.
                </p>
                <p>
                  <span className="text-foreground font-medium">Why the Experience Economy.</span>{" "}
                  Consumers are shifting spending from products to experiences. Immersive,
                  shareable, in-person moments are the fastest-growing segment of live
                  entertainment.
                </p>
                <p>
                  <span className="text-foreground font-medium">Current stage.</span> ClutchCube
                  is raising $10M to open its flagship immersive sports entertainment venue.
                </p>
                <p>
                  <span className="text-foreground font-medium">Long-term vision.</span> A
                  franchised, globally scaled network of ClutchCube venues in premier
                  entertainment districts worldwide.
                </p>
              </div>
            </div>
            <div className="relative aspect-[4/5] rounded-sm overflow-hidden border border-border/60">
              <img
                src={img2}
                alt="ClutchCube immersive concept"
                className="absolute inset-0 w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-background via-background/20 to-transparent" />
            </div>
          </div>
        </section>

        {/* FAST FACTS */}
        <section className="relative py-24 border-b border-border">
          <div className="mx-auto max-w-6xl px-6">
            <div className="max-w-2xl mb-12">
              <p className="text-xs uppercase tracking-[0.3em] text-primary mb-4">Fast Facts</p>
              <h2 className="font-display text-4xl sm:text-5xl leading-[1.05]">
                The essentials, at a glance.
              </h2>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {facts.map((f) => (
                <div
                  key={f.label}
                  className="border border-border/70 bg-card/40 backdrop-blur-sm p-6 rounded-sm hover:border-primary/40 transition"
                >
                  <p className="text-[11px] uppercase tracking-[0.25em] text-muted-foreground">
                    {f.label}
                  </p>
                  <p className="mt-3 font-display text-lg text-foreground leading-snug">
                    {f.value}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* MEDIA RESOURCES */}
        <section className="relative py-24 border-b border-border overflow-hidden">
          <img
            src={img6}
            alt=""
            aria-hidden
            className="absolute inset-0 w-full h-full object-cover opacity-10"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-background via-background/90 to-background" />
          <div className="relative mx-auto max-w-6xl px-6">
            <div className="max-w-2xl mb-12">
              <p className="text-xs uppercase tracking-[0.3em] text-primary mb-4">
                Media Resources
              </p>
              <h2 className="font-display text-4xl sm:text-5xl leading-[1.05]">
                Assets for editorial &amp; publication use.
              </h2>
              <p className="mt-6 text-muted-foreground">
                Downloadable media kit assets. Additional materials available on request.
              </p>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {resources.map((r) => (
                <div
                  key={`${r.title}-${r.type}`}
                  className="border border-border/70 bg-card/60 p-6 rounded-sm flex flex-col justify-between min-h-[140px]"
                >
                  <div>
                    <p className="font-display text-lg text-foreground">{r.title}</p>
                    <p className="mt-1 text-[11px] uppercase tracking-[0.25em] text-muted-foreground">
                      {r.type}
                    </p>
                  </div>
                  <div className="mt-6">
                    {r.available ? (
                      <span className="inline-flex items-center px-3 py-1.5 text-[11px] uppercase tracking-[0.25em] bg-primary text-primary-foreground rounded-sm">
                        Download
                      </span>
                    ) : (
                      <span className="inline-flex items-center px-3 py-1.5 text-[11px] uppercase tracking-[0.25em] border border-border/70 text-muted-foreground rounded-sm">
                        Coming Soon
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* IN THE NEWS */}
        <section className="relative py-24 border-b border-border">
          <div className="mx-auto max-w-6xl px-6">
            <div className="max-w-2xl mb-10">
              <p className="text-xs uppercase tracking-[0.3em] text-primary mb-4">In the News</p>
              <h2 className="font-display text-4xl sm:text-5xl leading-[1.05]">
                Company announcements &amp; coverage.
              </h2>
            </div>
            <div className="border border-dashed border-border/70 rounded-sm p-12 text-center bg-card/30">
              <p className="text-muted-foreground">
                Media coverage and company announcements will appear here.
              </p>
            </div>
          </div>
        </section>

        {/* SPEAKING */}
        <section className="relative py-24 border-b border-border overflow-hidden">
          <img
            src={img3}
            alt=""
            aria-hidden
            className="absolute inset-0 w-full h-full object-cover opacity-15"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-background via-background/90 to-background" />
          <div className="relative mx-auto max-w-6xl px-6 grid lg:grid-cols-2 gap-16 items-start">
            <div>
              <p className="text-xs uppercase tracking-[0.3em] text-primary mb-4">
                Speaking Opportunities
              </p>
              <h2 className="font-display text-4xl sm:text-5xl leading-[1.05]">
                Herman DeBoard is available for select engagements.
              </h2>
              <p className="mt-6 text-muted-foreground leading-relaxed">
                Founder Herman DeBoard speaks with media outlets, podcasts, and industry
                conferences on the future of sports entertainment, the Experience Economy, and
                what it takes to build a category-defining franchise from the ground up.
              </p>
            </div>
            <div className="flex flex-wrap gap-3">
              {speakingTopics.map((t) => (
                <span
                  key={t}
                  className="px-4 py-2 border border-border/70 bg-card/60 rounded-sm text-sm text-foreground"
                >
                  {t}
                </span>
              ))}
            </div>
          </div>
        </section>

        {/* INTERVIEW TOPICS */}
        <section className="relative py-24 border-b border-border">
          <div className="mx-auto max-w-6xl px-6">
            <div className="max-w-2xl mb-12">
              <p className="text-xs uppercase tracking-[0.3em] text-primary mb-4">
                Interview Topics
              </p>
              <h2 className="font-display text-4xl sm:text-5xl leading-[1.05]">
                Suggested conversations.
              </h2>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              {interviewTopics.map((t) => (
                <a
                  key={t}
                  href="#press-form"
                onClick={() => trackEvent("media_cta_click", { placement: "media_page" })}
                  className="group border border-border/70 bg-card/40 p-6 rounded-sm hover:border-primary/50 hover:bg-card/70 transition flex items-center justify-between gap-6"
                >
                  <span className="font-display text-lg text-foreground leading-snug">{t}</span>
                  <span className="text-primary text-xs uppercase tracking-[0.25em] shrink-0 opacity-70 group-hover:opacity-100 transition">
                    Request &rarr;
                  </span>
                </a>
              ))}
            </div>
          </div>
        </section>

        {/* PHOTOGRAPHY STRIP */}
        <section className="relative border-b border-border">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-0">
            {[img1, img5, img8, img6].map((im, i) => (
              <div key={i} className="relative aspect-square overflow-hidden">
                <img
                  src={im}
                  alt="ClutchCube concept imagery"
                  className="absolute inset-0 w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-background/20" />
              </div>
            ))}
          </div>
        </section>

        {/* PRESS CONTACT FORM */}
        <section id="press-form" className="relative py-24 border-b border-border overflow-hidden">
          <div className="absolute inset-0 led-grid opacity-15" />
          <div className="relative mx-auto max-w-4xl px-6">
            <div className="max-w-2xl mb-10">
              <p className="text-xs uppercase tracking-[0.3em] text-primary mb-4">
                Press Contact
              </p>
              <h2 className="font-display text-4xl sm:text-5xl leading-[1.05]">
                Submit a media request.
              </h2>
              <p className="mt-6 text-muted-foreground">
                Tell us about your outlet and how we can help. We aim to respond within two
                business days.
              </p>
            </div>

            {submitted ? (
              <div className="border border-primary/40 bg-card/60 p-8 rounded-sm">
                <p className="font-display text-2xl text-foreground">Thank you.</p>
                <p className="mt-3 text-muted-foreground">
                  Your media request has been received. Our team will follow up shortly at the
                  email you provided.
                </p>
              </div>
            ) : (
              <form onSubmit={onSubmit} onFocus={onFirstInteraction} onChange={onFirstInteraction} className="grid sm:grid-cols-2 gap-5">
                <div
                  aria-hidden="true"
                  style={{ position: "absolute", left: "-9999px", top: "-9999px", height: 0, width: 0, overflow: "hidden" }}
                >
                  <label htmlFor="hp-website">Website</label>
                  <input id="hp-website" name="hp_website" type="text" tabIndex={-1} autoComplete="off" />
                </div>
                <FormField label="Name" name="name" required />
                <FormField label="Publication" name="publication" required />
                <FormField label="Email" name="email" type="email" required />
                <FormField label="Phone" name="phone" type="tel" />
                <FormField label="Website" name="website" type="url" className="sm:col-span-2" />
                <div className="sm:col-span-2">
                  <label className="block text-[11px] uppercase tracking-[0.25em] text-muted-foreground mb-2">
                    Type of Request
                  </label>
                  <select
                    name="requestType"
                    required
                    defaultValue=""
                    className="w-full bg-transparent border border-border/70 rounded-sm px-4 py-3 text-foreground focus:outline-none focus:border-primary/60"
                  >
                    <option value="" disabled className="bg-background">
                      Select a type
                    </option>
                    {requestTypes.map((t) => (
                      <option key={t} value={t} className="bg-background">
                        {t}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="sm:col-span-2">
                  <label className="block text-[11px] uppercase tracking-[0.25em] text-muted-foreground mb-2">
                    Message
                  </label>
                  <textarea
                    name="message"
                    rows={5}
                    required
                    className="w-full bg-transparent border border-border/70 rounded-sm px-4 py-3 text-foreground focus:outline-none focus:border-primary/60"
                  />
                </div>
                {error && <p className="sm:col-span-2 text-sm text-destructive">{error}</p>}
                <div className="sm:col-span-2">
                  <button
                    type="submit"
                    disabled={submitting}
                    className="inline-flex items-center justify-center px-10 py-4 bg-primary text-primary-foreground font-semibold uppercase tracking-widest text-sm rounded-sm shadow-glow hover:translate-y-[-2px] transition disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    {submitting ? "Sending…" : "Submit Media Request"}
                  </button>
                </div>
              </form>
            )}

          </div>
        </section>

        {/* MEDIA CONTACT */}
        <section className="relative py-20">
          <div className="mx-auto max-w-4xl px-6 text-center">
            <p className="text-xs uppercase tracking-[0.3em] text-primary mb-4">
              Media Contact
            </p>
            <h2 className="font-display text-3xl sm:text-4xl leading-tight">Media Relations</h2>
            <a
              href="mailto:media@clutchcube.com"
              className="mt-6 inline-block text-lg text-foreground hover:text-primary transition"
            >
              media@clutchcube.com
            </a>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}

function FormField({
  label,
  name,
  type = "text",
  required,
  className,
}: {
  label: string;
  name: string;
  type?: string;
  required?: boolean;
  className?: string;
}) {
  return (
    <div className={className}>
      <label className="block text-[11px] uppercase tracking-[0.25em] text-muted-foreground mb-2">
        {label}
        {required && <span className="text-primary"> *</span>}
      </label>
      <input
        name={name}
        type={type}
        required={required}
        className="w-full bg-transparent border border-border/70 rounded-sm px-4 py-3 text-foreground focus:outline-none focus:border-primary/60"
      />
    </div>
  );
}
